import { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { X, Plus, Loader2, Search, UserPlus, AlertTriangle } from 'lucide-react';

function SelectField({ label, value, onChange, options, testId }) {
    return (
        <div>
            <label className="text-xs font-medium text-muted-foreground">{label}</label>
            <select value={value} onChange={onChange} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid={testId}>
                {options}
            </select>
        </div>
    );
}

function FormField({ label, value, onChange, type, placeholder, required, testId }) {
    return (
        <div>
            <label className="text-xs font-medium text-muted-foreground">{label}{required ? ' *' : ''}</label>
            <Input value={value} onChange={onChange} type={type || 'text'} placeholder={placeholder} data-testid={testId} />
        </div>
    );
}

export default function ManualBookingModal({ authAxios, sessions, slots, selectedDate, selectedTime, selectedSessionId, onClose, onSuccess }) {
    var [saving, setSaving] = useState(false);
    var [error, setError] = useState('');
    var [searchQuery, setSearchQuery] = useState('');
    var [searchResults, setSearchResults] = useState([]);
    var [searching, setSearching] = useState(false);
    var [showDupOverride, setShowDupOverride] = useState(false);
    var [showCapOverride, setShowCapOverride] = useState(false);

    var [firstName, setFirstName] = useState('');
    var [lastName, setLastName] = useState('');
    var [email, setEmail] = useState('');
    var [mobile, setMobile] = useState('');
    var [emergencyContact, setEmergencyContact] = useState('');
    var [dogName, setDogName] = useState('');
    var [dogBreed, setDogBreed] = useState('');
    var [dogAge, setDogAge] = useState('');
    var [dogSex, setDogSex] = useState('');
    var [dogNeutered, setDogNeutered] = useState('');
    var [dogWorking, setDogWorking] = useState('');
    var [healthConditions, setHealthConditions] = useState('');
    var [behaviourNotes, setBehaviourNotes] = useState('');
    var [additionalNotes, setAdditionalNotes] = useState('');
    var [sessionId, setSessionId] = useState(selectedSessionId || '');
    var [slotId, setSlotId] = useState('');
    var [bookingStatus, setBookingStatus] = useState('confirmed');
    var [paymentStatus, setPaymentStatus] = useState('paid');
    var [amountPaid, setAmountPaid] = useState('');
    var [paymentMethod, setPaymentMethod] = useState('cash');
    var [datePaid, setDatePaid] = useState(new Date().toISOString().slice(0, 10));
    var [adminNotes, setAdminNotes] = useState('');
    var [overrideDuplicate, setOverrideDuplicate] = useState(false);
    var [overrideCapacity, setOverrideCapacity] = useState(false);

    useEffect(function() {
        if (selectedDate && selectedTime && !slotId) {
            var match = slots.find(function(s) {
                return s.date === selectedDate && s.time === selectedTime && (!sessionId || s.session_id === sessionId);
            });
            if (match) setSlotId(match.id);
        }
    }, [selectedDate, selectedTime, sessionId, slots, slotId]);

    useEffect(function() {
        if (sessionId && !amountPaid) {
            var sess = sessions.find(function(s) { return s.id === sessionId; });
            if (sess) setAmountPaid(String(sess.price));
        }
    }, [sessionId, sessions, amountPaid]);

    var doSearch = useCallback(function() {
        if (searchQuery.length < 2) { setSearchResults([]); return; }
        setSearching(true);
        authAxios.get('/admin/customers/search?q=' + encodeURIComponent(searchQuery))
            .then(function(res) { setSearchResults(res.data); setSearching(false); })
            .catch(function() { setSearching(false); });
    }, [searchQuery, authAxios]);

    useEffect(function() {
        var timer = setTimeout(doSearch, 400);
        return function() { clearTimeout(timer); };
    }, [doSearch]);

    function selectCustomer(cust, dog) {
        var parts = cust.handler_name.split(' ');
        setFirstName(parts[0] || '');
        setLastName(parts.slice(1).join(' ') || '');
        setEmail(cust.email || '');
        setMobile(cust.phone || '');
        setEmergencyContact(cust.phone || '');
        if (dog) {
            setDogName(dog.name || '');
            setDogBreed(dog.breed || '');
            setDogAge(dog.age || '');
            setDogSex(dog.sex || '');
            setHealthConditions(dog.health || '');
            setBehaviourNotes(dog.behaviour || '');
        }
        setSearchQuery('');
        setSearchResults([]);
    }

    function handleSave() {
        if (!firstName.trim()) { setError('First name is required'); return; }
        if (!lastName.trim()) { setError('Last name is required'); return; }
        if (!mobile.trim()) { setError('Mobile number is required'); return; }
        if (!dogName.trim()) { setError('Dog name is required'); return; }
        if (!sessionId) { setError('Please select a session'); return; }
        if (!slotId) { setError('Please select a time slot'); return; }
        setError('');
        setSaving(true);
        authAxios.post('/admin/bookings/manual', {
            first_name: firstName.trim(), last_name: lastName.trim(), email: email.trim(),
            mobile: mobile.trim(), emergency_contact: emergencyContact.trim(),
            dog_name: dogName.trim(), dog_breed: dogBreed.trim(), dog_age: dogAge.trim(),
            dog_sex: dogSex, dog_neutered: dogNeutered, dog_working: dogWorking,
            health_conditions: healthConditions.trim() || 'None',
            behaviour_notes: behaviourNotes.trim(), additional_notes: additionalNotes.trim(),
            session_id: sessionId, slot_id: slotId, booking_status: bookingStatus,
            payment_status: paymentStatus, amount_paid: amountPaid ? parseFloat(amountPaid) : null,
            payment_method: paymentMethod, date_paid: datePaid, admin_notes: adminNotes.trim(),
            override_duplicate: overrideDuplicate, override_capacity: overrideCapacity
        })
        .then(function(res) { setSaving(false); onSuccess(res.data); })
        .catch(function(e) {
            setSaving(false);
            var detail = e.response ? e.response.data.detail : String(e);
            setError(detail);
            if (detail.includes('Duplicate')) setShowDupOverride(true);
            if (detail.includes('full')) setShowCapOverride(true);
        });
    }

    var selectedSlot = slots.find(function(s) { return s.id === slotId; });
    var selectedSession = sessions.find(function(s) { return s.id === sessionId; });
    var maxCap = selectedSession ? selectedSession.max_participants : 4;
    var available = selectedSlot ? selectedSlot.available_spots : 0;
    var booked = maxCap - available;

    // Build search results HTML to avoid .map in JSX
    var searchResultsRef = useRef(null);
    useEffect(function() {
        if (!searchResultsRef.current) return;
        searchResultsRef.current.innerHTML = '';
        searchResults.forEach(function(cust) {
            var div = document.createElement('div');
            div.className = 'p-3 hover:bg-gray-50 cursor-pointer border-b';
            var name = document.createElement('div');
            name.className = 'font-medium text-sm';
            name.textContent = cust.handler_name + (cust.email ? ' — ' + cust.email : '');
            div.appendChild(name);
            var dogsDiv = document.createElement('div');
            dogsDiv.className = 'flex flex-wrap gap-1 mt-1';
            cust.dogs.forEach(function(dog) {
                var btn = document.createElement('button');
                btn.className = 'text-xs px-2 py-1 bg-green-50 text-green-700 rounded-full hover:bg-green-100';
                btn.textContent = dog.name + ' (' + (dog.breed || '?') + ')';
                btn.onclick = function() { selectCustomer(cust, dog); };
                dogsDiv.appendChild(btn);
            });
            var newBtn = document.createElement('button');
            newBtn.className = 'text-xs px-2 py-1 bg-gray-100 rounded-full hover:bg-gray-200';
            newBtn.textContent = '+ New dog';
            newBtn.onclick = function() { selectCustomer(cust, null); };
            dogsDiv.appendChild(newBtn);
            div.appendChild(dogsDiv);
            searchResultsRef.current.appendChild(div);
        });
    }, [searchResults]);

    // Build session/slot options to avoid .map in JSX
    var sessionOpts = '<option value="">Select session</option>';
    sessions.forEach(function(s) { sessionOpts += '<option value="' + s.id + '">' + s.name + ' (£' + s.price + ')</option>'; });

    var matchingSlots = slots.filter(function(s) {
        return (!sessionId || s.session_id === sessionId) && (!selectedDate || s.date === selectedDate);
    });
    var slotOpts = '<option value="">Select slot</option>';
    matchingSlots.forEach(function(s) { slotOpts += '<option value="' + s.id + '">' + s.date + ' ' + s.time + ' (' + s.available_spots + ' left)</option>'; });

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-8 pb-8 overflow-y-auto" style={{ background: 'rgba(0,0,0,0.5)' }} data-testid="manual-booking-modal">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4">
                <div className="flex items-center justify-between p-5 border-b bg-primary/5 rounded-t-xl">
                    <div className="flex items-center gap-2">
                        <UserPlus className="w-5 h-5 text-primary" />
                        <h2 className="text-lg font-bold font-serif">Add Manual Booking</h2>
                        {selectedDate && <Badge className="bg-primary/10 text-primary ml-2">{selectedDate}</Badge>}
                    </div>
                    <Button variant="ghost" size="sm" onClick={onClose}><X className="w-5 h-5" /></Button>
                </div>

                <div className="p-5 space-y-5 max-h-[70vh] overflow-y-auto">
                    {slotId && (
                        <div className={'flex items-center justify-between p-3 rounded-lg text-sm ' + (available <= 0 ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200')}>
                            <span>Current Capacity: <strong>{booked} / {maxCap}</strong></span>
                            {available <= 0 ? <Badge className="bg-red-600 text-white">FULL</Badge> : <span className="text-green-700">{available} left</span>}
                        </div>
                    )}

                    <div>
                        <div className="relative">
                            <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
                            <Input placeholder="Search existing customer..." value={searchQuery} onChange={function(e) { setSearchQuery(e.target.value); }} className="pl-10" data-testid="customer-search" />
                            {searching && <Loader2 className="w-4 h-4 absolute right-3 top-3 animate-spin text-muted-foreground" />}
                        </div>
                        <div ref={searchResultsRef} className={searchResults.length > 0 ? 'mt-1 border rounded-lg max-h-48 overflow-y-auto bg-white shadow-lg' : ''} />
                    </div>

                    {error && (
                        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            <div>
                                <p>{error}</p>
                                {showDupOverride && <label className="flex items-center gap-2 mt-2 cursor-pointer"><input type="checkbox" checked={overrideDuplicate} onChange={function(e) { setOverrideDuplicate(e.target.checked); }} /><span className="font-medium">Override Duplicate</span></label>}
                                {showCapOverride && <label className="flex items-center gap-2 mt-2 cursor-pointer"><input type="checkbox" checked={overrideCapacity} onChange={function(e) { setOverrideCapacity(e.target.checked); }} /><span className="font-medium">Override Capacity</span></label>}
                            </div>
                        </div>
                    )}

                    <fieldset className="border border-border/50 rounded-lg p-4">
                        <legend className="text-sm font-semibold text-primary px-2">Customer Details</legend>
                        <div className="grid grid-cols-2 gap-3">
                            <FormField label="First Name" value={firstName} onChange={function(e){setFirstName(e.target.value);}} required testId="mb-first-name" />
                            <FormField label="Last Name" value={lastName} onChange={function(e){setLastName(e.target.value);}} required testId="mb-last-name" />
                            <FormField label="Email" value={email} onChange={function(e){setEmail(e.target.value);}} type="email" />
                            <FormField label="Mobile" value={mobile} onChange={function(e){setMobile(e.target.value);}} required />
                            <div className="col-span-2"><FormField label="Emergency Contact" value={emergencyContact} onChange={function(e){setEmergencyContact(e.target.value);}} /></div>
                        </div>
                    </fieldset>

                    <fieldset className="border border-border/50 rounded-lg p-4">
                        <legend className="text-sm font-semibold text-primary px-2">Dog Details</legend>
                        <div className="grid grid-cols-2 gap-3">
                            <FormField label="Dog Name" value={dogName} onChange={function(e){setDogName(e.target.value);}} required testId="mb-dog-name" />
                            <FormField label="Breed" value={dogBreed} onChange={function(e){setDogBreed(e.target.value);}} />
                            <FormField label="Age" value={dogAge} onChange={function(e){setDogAge(e.target.value);}} />
                            <SelectField label="Sex" value={dogSex} onChange={function(e){setDogSex(e.target.value);}} options={<><option value="">Select</option><option value="Male">Male</option><option value="Female">Female</option></>} />
                            <SelectField label="Neutered" value={dogNeutered} onChange={function(e){setDogNeutered(e.target.value);}} options={<><option value="">Select</option><option value="Yes">Yes</option><option value="No">No</option></>} />
                            <SelectField label="Working Dog" value={dogWorking} onChange={function(e){setDogWorking(e.target.value);}} options={<><option value="">Select</option><option value="Yes">Yes</option><option value="No">No</option></>} />
                        </div>
                    </fieldset>

                    <fieldset className="border border-border/50 rounded-lg p-4">
                        <legend className="text-sm font-semibold text-primary px-2">Health & Notes</legend>
                        <div className="space-y-3">
                            <FormField label="Health Conditions" value={healthConditions} onChange={function(e){setHealthConditions(e.target.value);}} placeholder="None" />
                            <FormField label="Behaviour Notes" value={behaviourNotes} onChange={function(e){setBehaviourNotes(e.target.value);}} />
                            <FormField label="Additional Notes" value={additionalNotes} onChange={function(e){setAdditionalNotes(e.target.value);}} />
                        </div>
                    </fieldset>

                    <fieldset className="border border-border/50 rounded-lg p-4">
                        <legend className="text-sm font-semibold text-primary px-2">Booking Details</legend>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs font-medium text-muted-foreground">Session *</label>
                                <select value={sessionId} onChange={function(e){setSessionId(e.target.value);setSlotId('');setAmountPaid('');}} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="mb-session" dangerouslySetInnerHTML={{__html: sessionOpts}} />
                            </div>
                            <div>
                                <label className="text-xs font-medium text-muted-foreground">Time Slot *</label>
                                <select value={slotId} onChange={function(e){setSlotId(e.target.value);}} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="mb-slot" dangerouslySetInnerHTML={{__html: slotOpts}} />
                            </div>
                            <SelectField label="Booking Status" value={bookingStatus} onChange={function(e){setBookingStatus(e.target.value);}} options={<><option value="confirmed">Confirmed</option><option value="pending">Pending</option><option value="cancelled">Cancelled</option></>} />
                            <SelectField label="Payment Status" value={paymentStatus} onChange={function(e){setPaymentStatus(e.target.value);}} options={<><option value="paid">Paid</option><option value="unpaid">Unpaid</option></>} />
                            <FormField label="Amount Paid (£)" value={amountPaid} onChange={function(e){setAmountPaid(e.target.value);}} type="number" />
                            <SelectField label="Payment Method" value={paymentMethod} onChange={function(e){setPaymentMethod(e.target.value);}} options={<><option value="cash">Cash</option><option value="bank_transfer">Bank Transfer</option><option value="card_machine">Card Machine</option><option value="stripe">Stripe</option><option value="gift_voucher">Gift Voucher</option><option value="complimentary">Complimentary</option><option value="other">Other</option></>} />
                            <FormField label="Date Paid" value={datePaid} onChange={function(e){setDatePaid(e.target.value);}} type="date" />
                            <div className="col-span-2"><FormField label="Internal Admin Notes" value={adminNotes} onChange={function(e){setAdminNotes(e.target.value);}} placeholder="Only visible to admin" /></div>
                        </div>
                    </fieldset>
                </div>

                <div className="flex items-center justify-between p-5 border-t bg-secondary/20 rounded-b-xl">
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={handleSave} disabled={saving} className="bg-primary" data-testid="mb-save">
                        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                        Save Booking
                    </Button>
                </div>
            </div>
        </div>
    );
}
